import Decimal from "decimal.js";
export var OddsFormat;
(function (OddsFormat) {
    OddsFormat["AMERICAN"] = "american";
    OddsFormat["FRACTIONAL"] = "fractional";
    OddsFormat["HONGKONG"] = "hongkong";
    OddsFormat["MALAYSIAN"] = "malaysian";
    OddsFormat["INDONESIAN"] = "indonesian";
    OddsFormat["DECIMAL"] = "decimal";
})(OddsFormat || (OddsFormat = {}));
/**
 * Rounds a number to the specified number of decimal places.
 * @param number - The number to round.
 * @param decimals - The number of decimal places (default is 0).
 * @returns The rounded number.
 */
const roundNumber = (number, decimals = 0) => Math.round((number * 10 ** decimals) / 10 ** decimals);
/**
 * Calculates the greatest common divisor (GCD) of two numbers.
 * @param a - The first number.
 * @param b - The second number.
 * @returns The greatest common divisor of `a` and `b`.
 */
const getGreatestCommonDivisor = (a, b) => b ? getGreatestCommonDivisor(b, a % b) : a;
/**
 * Converts an integer representation of odds into a decimal format.
 * @param odds - The integer odds value.
 * @param divider - The divider to convert the odds (default is 10000).
 * @returns The decimal representation of the odds.
 */
const convertIntegerToDecimal = (odds, divider) => new Decimal(odds).dividedBy(divider).toDecimalPlaces(2).toNumber();
/**
 * Converts decimal odds to American odds format.
 * @param odds - The decimal odds.
 * @returns The American odds as a string.
 */
const convertToAmericanOdds = (odds) => {
    if (odds <= 1)
        return "+0";
    if (odds >= 2)
        return `+${Math.round((odds - 1) * 100)}`;
    return `-${Math.round(Math.abs(100 / (odds - 1)))}`;
};
/**
 * Converts decimal odds to Hong Kong odds format.
 * @param odds - The decimal odds.
 * @returns The Hong Kong odds as a string.
 */
const convertToHongKongOdds = (odds) => (odds <= 1 ? -1 * (1 / (odds - 1)) : odds - 1).toFixed(2);
/**
 * Converts decimal odds to Indonesian odds format.
 * @param odds - The decimal odds.
 * @returns The Indonesian odds as a string.
 */
const convertToIndonesianOdds = (odds) => (odds > 2 ? odds - 1 : -1 / (odds - 1)).toFixed(2);
/**
 * Converts decimal odds to Malaysian odds format.
 * @param odds - The decimal odds.
 * @returns The Malaysian odds as a string.
 */
const convertToMalaysianOdds = (odds) => (odds >= 2 ? -1 / (odds - 1) : odds - 1).toFixed(2);
/**
 * Converts decimal odds to fractional odds format.
 * @param odds - The decimal odds.
 * @returns The fractional odds as a string.
 */
const convertToFractionalOdds = (odds) => {
    const oddsMap = {
        "303/10000": "1/33",
        "143/1000": "1/7",
        "167/1000": "1/6",
        "111/500": "2/9",
        "143/500": "2/7",
        "333/1000": "1/3",
        "91/250": "4/11",
        "111/250": "4/9",
        "533/1000": "8/15",
        "571/1000": "4/7",
        "123/200": "8/13",
        "667/1000": "4/6",
        "727/1000": "8/11",
        "833/1000": "5/6",
        "909/1000": "10/11",
        "3333/1000": "10/3",
    };
    const closestOdd = [
        1.01, 1.02, 1.0303, 1.04, 1.1, 1.125, 1.143, 1.167, 1.2, 1.222, 1.25, 1.286, 1.3, 1.333, 1.364,
        1.4, 1.444, 1.5, 1.533, 1.571, 1.615, 1.667, 1.727, 1.8, 1.833, 1.909, 2.0, 2.1, 2.2, 2.25, 2.3,
        2.375, 2.5, 2.625, 2.75, 3.0, 3.2, 3.25, 3.4, 3.5, 3.75, 4.0, 4.333, 4.5, 5.0, 5.5, 6.0, 6.5,
        7.0, 7.5, 8.0, 8.5, 9.0, 9.5, 10.0, 11.0, 12.0, 13.0, 15.0, 17.0, 21.0, 26.0, 34.0, 41.0, 51.0,
        67.0, 101.0,
    ].reduce((a, b) => (Math.abs(b - odds) < Math.abs(a - odds) ? b : a));
    const closestValue = Math.abs(closestOdd - odds) < 1 ? closestOdd : odds;
    const decimals = String(closestValue).includes(".")
        ? String(closestValue).split(".")[1].length
        : 1;
    const multiplier = 10 ** decimals;
    const numerator = roundNumber((closestValue - 1) * multiplier, decimals);
    const denominator = getGreatestCommonDivisor(numerator, multiplier);
    const fraction = `${numerator / denominator}/${multiplier / denominator}`;
    return oddsMap[fraction] || fraction;
};
/**
 * Formats odds into the specified format.
 * @param odds - The integer odds to format.
 * @param type - The target odds format (default is empty for decimal odds).
 * @param divider - The divider to convert odds to decimal (default is 10000).
 * @returns The formatted odds as a string.
 */
export const formatOdd = (odds, type = "", divider = 10000) => {
    if (!odds)
        return "";
    const decimalOdds = convertIntegerToDecimal(odds, divider);
    switch (type) {
        case OddsFormat.AMERICAN:
            return convertToAmericanOdds(decimalOdds);
        case OddsFormat.FRACTIONAL:
            return convertToFractionalOdds(decimalOdds);
        case OddsFormat.HONGKONG:
            return convertToHongKongOdds(decimalOdds);
        case OddsFormat.MALAYSIAN:
            return convertToMalaysianOdds(decimalOdds);
        case OddsFormat.INDONESIAN:
            return convertToIndonesianOdds(decimalOdds);
        case OddsFormat.DECIMAL:
        default:
            return decimalOdds.toFixed(2);
    }
};
